/**
 * DataBest — PerfRadarChart
 * 4-axis radar of the caregiver's core KPIs (normalized 0-100)
 * with optional overlay of peer group average.
 */
import React, { useMemo } from "react";
import {
    Radar,
    RadarChart,
    PolarGrid,
    PolarAngleAxis,
    PolarRadiusAxis,
    ResponsiveContainer,
    Tooltip,
    Legend,
} from "recharts";

const AXES = [
    { key: "records_completed", label: "Records Completed" },
    { key: "on_time", label: "On-time" },
    { key: "report_quality", label: "Report Quality" },
    { key: "coverage_gap", label: "Coverage Gap" },
];

const normalizeKPIs = (k) => {
    if (!k) return null;
    return {
        records_completed: k.pct_records_completed ?? 0,
        on_time: k.on_time_rate ?? 0,
        report_quality: k.report_quality_pct ?? 0,
        coverage_gap: k.coverage_gap_pct ?? 0,
    };
};

const PerfRadarChart = ({ kpis, peerKpis }) => {
    const data = useMemo(() => {
        const my = normalizeKPIs(kpis);
        const peer = normalizeKPIs(peerKpis);
        return AXES.map((a) => ({
            axis: a.label,
            you: my ? my[a.key] : 0,
            peer: peer ? peer[a.key] : null,
        }));
    }, [kpis, peerKpis]);

    return (
        <div
            className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
            data-testid="perf-radar"
        >
            <div className="mb-2 flex items-center justify-between">
                <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                    Performance shape
                </p>
                <p className="text-[10px] text-slate-400">4-axis, normalized 0–100</p>
            </div>
            <div className="h-56 w-full min-w-[10px] sm:h-64 lg:h-72">
                <ResponsiveContainer width="100%" height="100%" minWidth={10} minHeight={10}>
                    <RadarChart data={data} outerRadius="68%" margin={{ top: 12, right: 44, bottom: 12, left: 44 }}>
                        <PolarGrid stroke="#e2e8f0" />
                        <PolarAngleAxis
                            dataKey="axis"
                            tick={{ fontSize: 11, fill: "#475569", fontWeight: 700 }}
                        />
                        <PolarRadiusAxis
                            angle={90}
                            domain={[0, 100]}
                            tick={{ fontSize: 9, fill: "#94a3b8" }}
                        />
                        <Radar
                            name="You"
                            dataKey="you"
                            stroke="#1e40af"
                            fill="#1e40af"
                            fillOpacity={0.3}
                        />
                        {peerKpis && (
                            <Radar
                                name="Peer avg"
                                dataKey="peer"
                                stroke="#94a3b8"
                                fill="#94a3b8"
                                fillOpacity={0.12}
                                strokeDasharray="4 4"
                            />
                        )}
                        <Tooltip
                            contentStyle={{
                                fontSize: 11,
                                border: "1px solid #e2e8f0",
                                borderRadius: 6,
                            }}
                        />
                        <Legend
                            iconType="line"
                            wrapperStyle={{ fontSize: 10, paddingTop: 4 }}
                        />
                    </RadarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};

export default PerfRadarChart;
